import { useDrag } from "react-dnd";

/**
 * Represents an image asset object used in the Image Library.
 * Can include additional metadata fields as needed.
 *
 * @property {string} url - The image file URL (required).
 * @property {any} [key] - Optional. Any additional metadata (e.g., alt, id, etc.).
 */
export interface IImageAsset {
  /** The image file URL (required). */
  url: string;

  /** Optional additional metadata for the asset (e.g., alt, id, etc.). */
  [key: string]: any;
}

/**
 * Props for the DraggableAsset component, which makes an image asset draggable within the PageBuilder UI.
 *
 * @property {IImageAsset} asset - The image asset object to be made draggable.
 * @property {React.ReactNode} children - The rendered image or preview to display inside the draggable area.
 */
interface DraggableAssetProps {
  /** The image asset to be made draggable. */
  asset: IImageAsset;

  /** The image node or preview to display as the draggable child. */
  children: React.ReactNode;
}

/**
 * DraggableAsset – Drag-and-Drop Wrapper for Image Library Assets
 *
 * @component
 * @description
 * Makes an image asset in the Image Library draggable so it can be dropped onto
 * an ImageBlock, HeroSlider slide, or any other compatible drop target in the PageBuilder.
 * Uses react-dnd for drag handling and supports custom child rendering.
 *
 * @props
 * @param {IImageAsset} asset – The image asset object to be made draggable (must have a `url`).
 * @param {React.ReactNode} children – The rendered image or asset preview (will be made draggable).
 *
 * @features
 * - Allows images to be dragged from the library to the canvas or slide
 * - Adjusts opacity during drag for visual feedback
 * - Disables pointer events while dragging to prevent interaction glitches
 * - Customizable: wraps any image or asset preview node
 *
 * @remarks
 * - Internal-use only for PageBuilder’s Image Library
 * - Drag type is "ASSET_IMAGE" (must match drop target’s accepted type)
 *
 * @example
 * <DraggableAsset asset={asset}>
 *   <img src={asset.url} alt="" />
 * </DraggableAsset>
 *
 * @since 2025-06-12
 * @lastUpdated 2025-07-15
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
export default function DraggableAsset({ asset, children }: DraggableAssetProps) {
	const [{ isDragging }, dragRef] = useDrag({
		type: "ASSET_IMAGE",
		item: { url: asset.url, asset },
		collect: (monitor) => ({
			isDragging: monitor.isDragging()
		})
	});

	return (
		<div
			ref={dragRef}
			style={{
				opacity: isDragging ? 0.4 : 1,
				cursor: "grab",
				pointerEvents: isDragging ? "none" : "auto",
			}}
		>
			{children}
		</div>
	);
}
